import { html, nothing } from "lit";
import { state } from "lit/decorators.js";

import "@phosphor-icons/webcomponents/PhCalendarBlank";
import "@phosphor-icons/webcomponents/PhClock";
import "ui-components/button/wow-button";
import "ui-components/tab-bar/tab-bar";
import type { TabItem } from "ui-components/tab-bar/tab-bar";
import "ui-components/centered-dialog/wow-centered-dialog";

import { styles } from "./message-form-page.css";
import { SignalElement, inject } from "ui-common/di";
import { LayoutController } from "ui-common/layout-controller";
import type { IAuthState } from "ui-common/services/auth/types";
import type { ICreatorMessageFormState, ICreatorMessageFormService } from "../../services/creator-messages-form/types";
import type { ICreatorMessagesService } from "../../services/creator-messages/types";
import { navigateTo } from "../../router";
import { t, translate } from "../../translations/i18n";
import { NotificationService } from "../../../../../libs/ui-components/notification";

type MessageTab = "content" | "settings";

export abstract class MessageFormPage extends SignalElement {
  static override styles = styles;

  @inject<IAuthState>("authState")
  protected authState!: IAuthState;

  @inject<ICreatorMessageFormState>("creatorMessageFormState")
  protected formState!: ICreatorMessageFormState;

  @inject<ICreatorMessageFormService>("creatorMessageFormService")
  protected formService!: ICreatorMessageFormService;

  @inject<ICreatorMessagesService>("creatorMessagesService")
  private messagesService!: ICreatorMessagesService;

  protected layoutController = new LayoutController(this);

  @state() protected activeTab: MessageTab = "content";
  @state() private showCancelConfirmation = false;
  @state() private showSendConfirmation = false;
  @state() private segmentsExpanded = false;
  @state() private segmentSearch = "";

  abstract get mode(): "create" | "edit";
  abstract get pageTitle(): ReturnType<typeof t>;
  abstract get pageSubtitle(): ReturnType<typeof t>;

  override connectedCallback(): void {
    super.connectedCallback();
    this.segmentsExpanded = this.mode === "edit";
    this.formService.setTranslationFunction((key: string) => translate(key as Parameters<typeof translate>[0]));
    this.formService.loadSegments();
  }

  override render() {
    if (this.layoutController.isMobile) {
      return this.renderMobileLayout();
    }
    return this.renderDesktopLayout();
  }

  private renderDesktopLayout() {
    return html`
      <div class="page-header">
        <h1 class="page-title">${this.pageTitle}</h1>
        <p class="page-subtitle">${this.pageSubtitle}</p>
      </div>
      <div class="page-layout">
        <div class="page-left-column">
          <h2 class="form-panel-label">${t("creatorMessages.sendSettings")}</h2>
          <div class="form-panel">${this.renderSendSettings()}</div>
        </div>
        <div class="page-right-column">
          <h2 class="content-panel-label">${t("creatorMessages.messageContent")}</h2>
          <div class="content-panel">${this.renderMessageContent()}</div>
        </div>
      </div>
      ${this.renderFooter()} ${this.renderCancelConfirmDialog()} ${this.renderSendConfirmDialog()}
    `;
  }

  private renderMobileLayout() {
    const tabs: TabItem[] = [
      { key: "content", label: translate("creatorMessages.messageContent"), disabled: false },
      { key: "settings", label: translate("creatorMessages.sendSettings"), disabled: false },
    ];

    return html`
      <div class="page-container">
        <div class="page-header">
          <h1 class="page-title mobile-title">${this.pageTitle}</h1>
        </div>
        <wow-tab-bar .tabs=${tabs} .activeTab=${this.activeTab} @tab-change=${this.handleTabChange}></wow-tab-bar>
        ${this.activeTab === "content"
          ? html`<div class="mobile-content-tab">${this.renderMessageContent()}</div>`
          : html`<div class="mobile-settings-tab">${this.renderSendSettings()}</div>`}
      </div>
      ${this.renderFooter()} ${this.renderCancelConfirmDialog()} ${this.renderSendConfirmDialog()}
    `;
  }

  private renderSendSettings() {
    const formData = this.formState.formData.get();
    const errors = this.formState.validationErrors.get();

    return html`
      <div class="toggle-field">
        <wow-switch
          label=${t("creatorMessages.scheduleToggle")}
          ?checked=${formData.scheduleEnabled}
          @change=${(e: Event) => {
            const checked = (e.target as HTMLInputElement).checked;
            this.formService.updateFormData("scheduleEnabled", checked);
          }}
        ></wow-switch>
        ${formData.scheduleEnabled
          ? html`
              <div class="toggle-sub-fields">
                <div class="schedule-row">
                  <div class="input-icon-wrapper">
                    <wow-input
                      type="date"
                      label=${t("creatorMessages.scheduledDate")}
                      .value=${formData.scheduledAt}
                      error-message=${errors.scheduledAt ?? ""}
                      min=${new Date().toISOString().substring(0, 10)}
                      @input=${(e: Event) =>
                        this.formService.updateFormData("scheduledAt", (e.target as HTMLInputElement).value)}
                    ></wow-input>
                    <ph-calendar-blank class="input-icon" size="20"></ph-calendar-blank>
                  </div>
                  <div class="input-icon-wrapper">
                    <wow-input
                      type="time"
                      label=${t("creatorMessages.scheduledTime")}
                      .value=${formData.scheduledTime}
                      error-message=${errors.scheduledTime ?? ""}
                      @input=${(e: Event) =>
                        this.formService.updateFormData("scheduledTime", (e.target as HTMLInputElement).value)}
                    ></wow-input>
                    <ph-clock class="input-icon" size="20"></ph-clock>
                  </div>
                </div>
              </div>
            `
          : nothing}
      </div>

      <div class="form-section">
        <span class="form-section-label">${t("creatorMessages.audienceLabel")}</span>
        ${this.renderSegmentPicker()}
        ${errors.segments ? html`<span class="segment-error">${errors.segments}</span>` : nothing}
      </div>

      <div class="toggle-field">
        <wow-switch
          label=${t("creatorMessages.ctaToggle")}
          ?checked=${formData.ctaEnabled}
          @change=${(e: Event) => {
            const checked = (e.target as HTMLInputElement).checked;
            this.formService.updateFormData("ctaEnabled", checked);
          }}
        ></wow-switch>
        ${formData.ctaEnabled
          ? html`
              <div class="toggle-sub-fields">
                <wow-input
                  label=${t("creatorMessages.ctaTextLabel")}
                  placeholder=${t("creatorMessages.ctaTextPlaceholder")}
                  .value=${formData.ctaText}
                  error-message=${errors.ctaText ?? ""}
                  @input=${(e: Event) =>
                    this.formService.updateFormData("ctaText", (e.target as HTMLInputElement).value)}
                ></wow-input>
                <wow-input
                  label=${t("creatorMessages.ctaUrlLabel")}
                  placeholder=${t("creatorMessages.ctaUrlPlaceholder")}
                  .value=${formData.ctaUrl}
                  error-message=${errors.ctaUrl ?? ""}
                  @input=${(e: Event) =>
                    this.formService.updateFormData("ctaUrl", (e.target as HTMLInputElement).value)}
                ></wow-input>
              </div>
            `
          : nothing}
      </div>
    `;
  }

  private renderMessageContent() {
    const formData = this.formState.formData.get();
    const errors = this.formState.validationErrors.get();

    return html`
      <div>
        <wow-input
          label=${t("creatorMessages.titleLabel")}
          placeholder=${t("creatorMessages.titlePlaceholder")}
          .value=${formData.title}
          error-message=${errors.title ?? ""}
          maxlength="50"
          @input=${(e: Event) => this.formService.updateFormData("title", (e.target as HTMLInputElement).value)}
        ></wow-input>
        <div class="char-count">${t("creatorMessages.maxChars50")}</div>
      </div>

      <div class="mobile-textarea-wrapper">
        <wow-textarea
          label=${t("creatorMessages.descriptionLabel")}
          placeholder=${t("creatorMessages.descriptionPlaceholder")}
          .value=${formData.content}
          error-message=${errors.content ?? ""}
          class="message-textarea"
          maxlength="1000"
          @input=${(e: Event) => this.formService.updateFormData("content", (e.target as HTMLTextAreaElement).value)}
        ></wow-textarea>
        <div class="char-count">${t("creatorMessages.maxChars1000")}</div>
      </div>
    `;
  }

  private getSegmentLabel(segment: { key: string; eventTitle?: string }): string {
    if (segment.key === "all-fans") return translate("creatorMessages.segmentAllFans");
    if (segment.key === "waitlist") return translate("creatorMessages.segmentWaitlist");
    if (segment.key.startsWith("live-event:") && segment.eventTitle) {
      return `${translate("creatorMessages.segmentLiveEventTicketHolders")} \u201C${segment.eventTitle}\u201D`;
    }
    return segment.key;
  }

  private renderSegmentPicker() {
    const availableSegments = this.formState.availableSegments.get();
    const segmentsLoading = this.formState.segmentsLoading.get();
    const selectedSegments = this.formState.formData.get().segments;
    const search = this.segmentSearch.toLowerCase();

    const filtered = search
      ? availableSegments.filter((s) => this.getSegmentLabel(s).toLowerCase().includes(search))
      : availableSegments;

    const toggleSegment = (key: string) => {
      const current = this.formState.formData.get().segments;
      const updated = current.includes(key) ? current.filter((k) => k !== key) : [...current, key];
      this.formService.updateFormData("segments", updated);
    };

    return html`
      <div class="segment-picker">
        <div class="segment-picker-header" @click=${() => (this.segmentsExpanded = !this.segmentsExpanded)}>
          <div class="segment-search-wrapper">
            <input
              class="segment-search"
              type="text"
              placeholder=${t("creatorMessages.audiencePlaceholder")}
              .value=${this.segmentSearch}
              @input=${(e: Event) => {
                this.segmentSearch = (e.target as HTMLInputElement).value;
                this.segmentsExpanded = true;
              }}
              @click=${(e: Event) => e.stopPropagation()}
            />
          </div>
          <span class="segment-chevron ${this.segmentsExpanded ? "expanded" : ""}"
            ><ph-caret-down size="20"></ph-caret-down
          ></span>
        </div>
        ${this.segmentsExpanded
          ? html`
              <div class="segment-picker-list">
                ${segmentsLoading
                  ? html`<div class="segment-loading">${translate("creatorMessages.segmentsLoading")}</div>`
                  : filtered.map(
                      (seg) => html`
                        <label class="segment-item" @click=${(e: Event) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            .checked=${selectedSegments.includes(seg.key)}
                            @change=${() => toggleSegment(seg.key)}
                          />
                          <span class="segment-label">${this.getSegmentLabel(seg)} (${seg.count})</span>
                        </label>
                      `,
                    )}
                ${!segmentsLoading && filtered.length === 0
                  ? html`<div class="segment-empty">${t("creatorMessages.errors.noSegmentsFound")}</div>`
                  : nothing}
              </div>
            `
          : nothing}
      </div>
    `;
  }

  private renderFooter() {
    if (this.layoutController.isMobile) {
      return this.renderMobileFooter();
    }
    return this.renderDesktopFooter();
  }

  private get isReadOnly(): boolean {
    const status = this.formState.messageStatus.get();
    return status === "sent" || status === "sending" || status === "failed";
  }

  private renderDesktopFooter() {
    if (this.isReadOnly) {
      return html`
        <div class="page-footer">
          <div class="footer-actions">
            <wow-button variant="neutral" size="small" @click=${() => this.navigateToList()}>
              ${t("creatorMessages.cancel")}
            </wow-button>
          </div>
        </div>
      `;
    }

    const formData = this.formState.formData.get();
    const isSubmitting = this.formState.isSubmitting.get();
    const primaryLabel = formData.scheduleEnabled ? t("creatorMessages.schedule") : t("creatorMessages.sendNow");

    return html`
      <div class="page-footer">
        <div class="footer-actions">
          <div class="footer-left">
            <wow-button variant="neutral" size="small" @click=${this.handleCancel}>
              ${t("creatorMessages.cancel")}
            </wow-button>
          </div>
          <div class="footer-right">
            <wow-button variant="neutral" size="small" ?disabled=${isSubmitting} @click=${this.handleSaveDraft}>
              ${isSubmitting ? t("creatorMessages.saving") : t("creatorMessages.saveDraft")}
            </wow-button>
            <wow-button variant="primary" size="small" ?disabled=${isSubmitting} @click=${this.handlePrimaryAction}>
              ${isSubmitting ? t("creatorMessages.saving") : primaryLabel}
            </wow-button>
          </div>
        </div>
      </div>
    `;
  }

  private renderMobileFooter() {
    if (this.isReadOnly) {
      return html`
        <div class="page-footer">
          <div class="footer-actions">
            <wow-button variant="neutral" size="small" @click=${() => this.navigateToList()}>
              ${t("creatorMessages.cancel")}
            </wow-button>
          </div>
        </div>
      `;
    }

    const formData = this.formState.formData.get();
    const isSubmitting = this.formState.isSubmitting.get();

    if (this.activeTab === "content") {
      return html`
        <div class="page-footer">
          <div class="footer-actions">
            <wow-button variant="neutral" size="small" @click=${this.handleCancel}>
              ${t("creatorMessages.cancel")}
            </wow-button>
            <wow-button variant="primary" size="small" @click=${() => (this.activeTab = "settings")}>
              ${t("creatorMessages.goToSettings")}
            </wow-button>
          </div>
        </div>
      `;
    }

    const primaryLabel = formData.scheduleEnabled ? t("creatorMessages.schedule") : t("creatorMessages.sendNow");

    return html`
      <div class="page-footer">
        <div class="footer-actions">
          <wow-button variant="neutral" size="small" @click=${this.handleCancel}>
            ${t("creatorMessages.cancel")}
          </wow-button>
          <wow-button variant="primary" size="small" ?disabled=${isSubmitting} @click=${this.handlePrimaryAction}>
            ${isSubmitting ? t("creatorMessages.saving") : primaryLabel}
          </wow-button>
        </div>
      </div>
    `;
  }

  private renderCancelConfirmDialog() {
    if (!this.showCancelConfirmation) return nothing;

    return html`
      <wow-centered-dialog
        header=${t("creatorMessages.cancelConfirmTitle")}
        description=${t("creatorMessages.cancelConfirmDescriptionDraft")}
        @close=${() => (this.showCancelConfirmation = false)}
      >
        <wow-button variant="primary" size="medium" @click=${this.handleSaveDraftAndLeave}>
          ${t("creatorMessages.saveDraft")}
        </wow-button>
        <wow-button variant="neutral" size="medium" @click=${() => (this.showCancelConfirmation = false)}>
          ${t("creatorMessages.cancelConfirmStay")}
        </wow-button>
        <wow-button variant="neutral" size="medium" @click=${this.handleLeave}>
          ${t("creatorMessages.cancelConfirmLeave")}
        </wow-button>
      </wow-centered-dialog>
    `;
  }

  private renderSendConfirmDialog() {
    if (!this.showSendConfirmation) return nothing;

    return html`
      <wow-centered-dialog
        header=${t("creatorMessages.sendConfirmTitle")}
        description=${t("creatorMessages.cancelConfirmDescriptionSchedule")}
        @close=${() => (this.showSendConfirmation = false)}
      >
        <wow-button variant="primary" size="medium" @click=${this.handleConfirmSend}>
          ${t("creatorMessages.sendNow")}
        </wow-button>
        <wow-button variant="neutral" size="medium" @click=${() => (this.showSendConfirmation = false)}>
          ${t("creatorMessages.cancelConfirmStay")}
        </wow-button>
      </wow-centered-dialog>
    `;
  }

  private handleTabChange = (e: CustomEvent<{ key: string }>) => {
    this.activeTab = e.detail.key as MessageTab;
  };

  private handleCancel = () => {
    this.showCancelConfirmation = true;
  };

  protected handleSaveDraft = async () => {
    const success = await this.formService.saveAsDraft();
    if (success) {
      this.navigateToList();
    } else {
      NotificationService.error(translate("creatorMessages.errors.saveFailed"));
    }
  };

  private handleSaveDraftAndLeave = async () => {
    this.showCancelConfirmation = false;
    const success = await this.formService.saveAsDraft();
    if (success) {
      this.navigateToList();
    }
  };

  private handleLeave = () => {
    this.showCancelConfirmation = false;
    this.formService.reset();
    this.navigateToList();
  };

  private handlePrimaryAction = () => {
    if (!this.formService.validateForm()) return;

    const formData = this.formState.formData.get();
    if (formData.scheduleEnabled) {
      this.doSaveAndSchedule();
    } else {
      this.showSendConfirmation = true;
    }
  };

  private handleConfirmSend = () => {
    this.showSendConfirmation = false;
    this.doSaveAndSchedule();
  };

  private doSaveAndSchedule = async () => {
    const success = await this.formService.saveAndSchedule();
    if (success) {
      this.navigateToList();
    }
  };

  protected async navigateToList() {
    this.messagesService.invalidateCache();
    await this.messagesService.loadMessages();
    navigateTo("messages", true);
  }
}
